import {mapMaybes} from '@freckle/maybe'
import type {
  CsvUnknownFileEncodingT,
  CsvParseErrorT,
  CsvMissingColumnT,
  SchoolNotFoundErrorT,
  DuplicateIdErrorT,
  DuplicateEmailT,
  StudentNotEnrolledT,
  EnrollmentStudentNotFoundT,
  EnrollmentCourseNotFoundT,
  CourseTeacherNotFoundT,
  StudentLanguageMalformedT,
  StudentLanguageUnsupportedT,
} from '@freckle/educator-entities/ts/common/models/csv-validation-errors/csv-errors'

export type CsvValidationErrorT =
  | CsvUnknownFileEncodingT
  | CsvParseErrorT
  | CsvMissingColumnT
  | SchoolNotFoundErrorT
  | DuplicateIdErrorT
  | DuplicateEmailT
  | StudentNotEnrolledT
  | EnrollmentStudentNotFoundT
  | EnrollmentCourseNotFoundT
  | CourseTeacherNotFoundT
  | StudentLanguageMalformedT
  | StudentLanguageUnsupportedT

type AffirmedErrorNameT = CsvValidationErrorT['name']

export type UnaffirmedValidationErrorT = {
  name: string
  message: string
}

/**
 * Here, affirmed errors are those errors that we've identified an exact type for.
 * Unaffirmed are those that do not yet have an exact known name and details body.
 */
type TaggedValidationErrorT =
  | {
      tag: 'affirmed'
      error: CsvValidationErrorT
    }
  | {
      tag: 'unaffirmed'
      error: UnaffirmedValidationErrorT
    }

export const CsvValidationErrors = {
  csvUnknownFileEncoding: 'csv-unknown-file-encoding' as AffirmedErrorNameT,
  csvParseError: 'csv-parse-exception' as AffirmedErrorNameT,
  csvMissingColumn: 'csv-missing-column' as AffirmedErrorNameT,
  schoolNotFound: 'school-not-found' as AffirmedErrorNameT,
  duplicateId: 'duplicate-id' as AffirmedErrorNameT,
  duplicateEmail: 'duplicate-email' as AffirmedErrorNameT,
  studentNotEnrolled: 'student-not-enrolled' as AffirmedErrorNameT,
  enrollmentStudentNotFound: 'enrollment-student-not-found' as AffirmedErrorNameT,
  enrollmentCourseNotFound: 'enrollment-course-not-found' as AffirmedErrorNameT,
  courseTeacherNotFound: 'course-teacher-not-found' as AffirmedErrorNameT,
  malformedLanguage: 'malformed-language' as AffirmedErrorNameT,
  unsupportedLanguage: 'unsupported-language' as AffirmedErrorNameT,

  all(): Array<AffirmedErrorNameT> {
    return [
      CsvValidationErrors.csvUnknownFileEncoding,
      CsvValidationErrors.csvParseError,
      CsvValidationErrors.csvMissingColumn,
      CsvValidationErrors.schoolNotFound,
      CsvValidationErrors.duplicateId,
      CsvValidationErrors.duplicateEmail,
      CsvValidationErrors.studentNotEnrolled,
      CsvValidationErrors.enrollmentStudentNotFound,
      CsvValidationErrors.enrollmentCourseNotFound,
      CsvValidationErrors.courseTeacherNotFound,
      CsvValidationErrors.malformedLanguage,
      CsvValidationErrors.unsupportedLanguage,
    ]
  },
}

const safeParseResponseValidationErrors = (
  response:
    | {
        response?: {
          json?: {validationErrors?: (CsvValidationErrorT | UnaffirmedValidationErrorT)[]}
        }
      }
    | undefined,
): null | (CsvValidationErrorT | UnaffirmedValidationErrorT)[] => {
  try {
    const validationErrors = response?.response?.json?.validationErrors
    if (validationErrors !== undefined) {
      return Array.isArray(validationErrors) ? validationErrors : []
    }
    return null
  } catch (_err) {
    console.warn('Error parsing JSON from response', response)
    return null
  }
}

export function parseErrorResponse(
  response:
    | {
        response?: {
          json?: {validationErrors?: (CsvValidationErrorT | UnaffirmedValidationErrorT)[]}
        }
      }
    | undefined,
): Array<TaggedValidationErrorT> {
  const mValidationErrors = safeParseResponseValidationErrors(response)

  if (mValidationErrors === null) {
    return [{tag: 'unaffirmed', error: {name: 'Unknown error', message: 'This operation failed.'}}]
  } else {
    return mValidationErrors.map(error => {
      const isAffirmed = CsvValidationErrors.all().includes(error.name as AffirmedErrorNameT)
      return isAffirmed
        ? {tag: 'affirmed' as const, error: error as CsvValidationErrorT}
        : {tag: 'unaffirmed' as const, error}
    })
  }
}

export const getAffirmedErrors = (
  errors: Array<TaggedValidationErrorT>,
): ReadonlyArray<CsvValidationErrorT> =>
  mapMaybes(errors, error => (error.tag === 'affirmed' ? error.error : null))

export const getUnaffirmedErrors = (
  errors: Array<TaggedValidationErrorT>,
): ReadonlyArray<UnaffirmedValidationErrorT> =>
  mapMaybes(errors, error => (error.tag === 'unaffirmed' ? error.error : null))
